import { useCallback, useMemo, useState } from 'react'
import { Attributes } from '@leverege/ui-attributes'
import { DateTime } from 'luxon'
import { useSharedContext } from '@leverege/ui-plugin'
import { Constants } from '../../../../util'

export default function useAisleSessionsBarChart( opts ) {
  const { result, data } = opts

  const [ type, setType ] = useState( 'count' )
  const { operatingHours } = useSharedContext()

  const onSetType = useCallback( ( { data } ) => {
    setType( data )
  }, [] )

  const chartData = useMemo( () => {
    const ret = Constants.DAYS_OF_WEEK.reduce( ( prev, curr ) => {
      prev[curr] = {
        dates : new Set(),
        aisles : data.items.reduce( ( acc, item ) => {
          const name = Attributes.get( 'name', item ) || item.data.name
          if ( name ) {
            acc[item.id] = {
              count : 0,
              name,
              sessions : new Map()
            }
          }
  
          return acc
        }, {} )
      }

      return prev
    }, {} )
    
    const start = DateTime.fromISO( result.start )
    const end = DateTime.fromMillis(
      Math.min(
        new Date( result.end ),
        DateTime.local().setZone( operatingHours.timezone, { keepLocalTime : true } )
      )
    )

    for ( let i = start; i < end; i = i.plus( { day : 1 } ) ) {
      const day = i.get( 'weekdayLong' )
      const date = i.toISODate()

      if ( ret[day] && !ret[day].dates.has( date ) ) {
        ret[day].dates.add( date )
      }
    }

    result?.items?.forEach( ( item ) => {
      const { time, data } = item
      const { extraData } = data
      const deviceId = data?.featureDevice?.deviceId
      const dateTime = DateTime.fromISO( time )
      const day = dateTime.get( 'weekdayLong' )

      if ( deviceId && ret[day].aisles[deviceId] ) {
        const datum = ret[day].aisles[deviceId]
        if ( !datum.sessions.has( extraData.detection?.objectId ) ) {
          datum.sessions.set( extraData.detection.objectId, item )
          datum.count += 1
        }
      }
    } )

    return Object
      .entries( ret )
      .map( ( [ day, data ] ) => {
        return {
          day,
          dates : data.dates,
          aisles : data.aisles
        }
      } )
  }, [ result, data ] )

  const config = useMemo( () => {
    const xAccessor = ( d ) => {
      return d.day
    }
    return {
      accessors : {
        aisles : data.items.reduce( ( prev, item ) => {
          const { id } = item

          prev[id] = {
            xAccessor,
            yAccessor : ( d ) => {
              if ( type === 'count' ) {
                return d.aisles[id].count
              }
  
              return Math.round( d.aisles[id].count / d.dates.size )
            }
          }
          return prev
        }, {} )
      },
      scale : {
        x : { type : 'band', padding : 0.3 },
        y : { type : 'linear' }
      },
      tickFormat : {
        x : ( value ) => {
          return value.slice( 0, 3 )
        }
      }
    }
  }, [ data, type ] )
  
  return {
    data : chartData,
    config,
    onSetType,
    type
  }
}
